import { Head, usePage } from '@inertiajs/react';
import { useMemo } from 'react';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heading } from '@/components/ui/heading';
import { InlineLink } from '@/components/ui/inline-link';
import type { JSONContent } from '@tiptap/react';


type Attachment = {
  id: number;
  url: string;
  filename: string;
};

type ContentBlock =
  | {
      type: 'hero';
      title: string;
      subtitle?: string;
      cta_label?: string;
      cta_href?: string;
    }
  | { type: 'rich_text'; doc: JSONContent }
  | { type: 'image'; attachment_id: number; alt: string; caption?: string }
  | { type: 'quote'; text: string; author?: string; role?: string; source?: string; source_url?: string }
  | { type: 'cta'; title: string; body?: string; cta_label: string; cta_href: string };

type BlogPost = {
  id: number;
  title: string;
  slug: string;
  caption?: string | null;
  seoTitle?: string | null;
  seoDescription?: string | null;
  publishedAt?: string | null;
  updatedAt?: string | null;
  newBadge?: boolean;
  ogImage?: Attachment | null;
  contentBlocks?: ContentBlock[];
  authorName?: string | null;
  authorRole?: string | null;
  authorAvatarUrl?: string | null;
};

type PageProps = {
  post?: BlogPost;
  related?: BlogPost[];
  contentImages?: Attachment[];
  error?: 'not_found';
};

const toArray = (value?: JSONContent[] | null) => (Array.isArray(value) ? value : []);

const shortBlogDateFormatter = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'Europe/Rome',
});

const fullBlogDateFormatter = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: 'long',
  year: 'numeric',
  timeZone: 'Europe/Rome',
});

const formatShortBlogDate = (value?: string | null) =>
  value ? shortBlogDateFormatter.format(new Date(value)) : null;

const formatFullBlogDate = (value?: string | null) =>
  value ? fullBlogDateFormatter.format(new Date(value)) : null;

const renderMarks = (text: string, marks?: { type: string; attrs?: Record<string, string> }[]) => {
  if (!marks || marks.length === 0) return text;
  return marks.reduce<React.ReactNode>((acc, mark) => {
    switch (mark.type) {
      case 'bold':
        return <strong>{acc}</strong>;
      case 'italic':
        return <em>{acc}</em>;
      case 'link': {
        const href = mark.attrs?.href ?? '';
        const isExternal = href.startsWith('http');
        return (
          <InlineLink
            href={href}
            rel="noreferrer"
            {...(isExternal && { target: '_blank' })}
          >
            {acc}
          </InlineLink>
        );
      }
      default:
        return acc;
    }
  }, text);
};

const renderNode = (node: JSONContent, index: number): React.ReactNode => {
  switch (node.type) {
    case 'paragraph':
      return (
        <p key={index} className="text-foreground">
          {toArray(node.content).map((child, idx) => renderNode(child, idx))}
        </p>
      );
    case 'heading':
      if (node.attrs?.level === 2) {
        return (
          <Heading key={index} level={2}>
            {toArray(node.content).map((child, idx) => renderNode(child, idx))}
          </Heading>
        );
      }
      return (
        <Heading key={index} level={3}>
          {toArray(node.content).map((child, idx) => renderNode(child, idx))}
        </Heading>
      );
    case 'bullet_list':
    case 'bulletList':
      return (
        <ul key={index} className="list-disc pl-6 space-y-2">
          {toArray(node.content).map(renderNode)}
        </ul>
      );
    case 'ordered_list':
    case 'orderedList':
      return (
        <ol key={index} className="list-decimal pl-6 space-y-2">
          {toArray(node.content).map(renderNode)}
        </ol>
      );
    case 'list_item':
    case 'listItem':
      return <li key={index}>{toArray(node.content).map(renderNode)}</li>;
    case 'text':
      return (
        <span key={index} className="text-foreground">
          {renderMarks(node.text ?? '', node.marks as { type: string; attrs?: Record<string, string> }[])}
        </span>
      );
    default:
      return null;
  }
};

export default function BlogShow() {
  const { props } = usePage<PageProps>();
  const { post, related = [], contentImages = [], error } = props;
  const seoDescription =
    post?.seoDescription ??
    post?.caption ??
    'Approfondimento Emovia su intelligenza artificiale, psicologia e pratica clinica.';

  const contentImageMap = useMemo(() => {
    return new Map(contentImages.map((image) => [image.id, image]));
  }, [contentImages]);

  const jsonLd = useMemo(() => {
    if (!post) return null;
    const postUrl = `https://www.emovia.com/blog/${post.slug}`;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: post.seoTitle ?? post.title,
        description: seoDescription,
        inLanguage: 'it-IT',
        isAccessibleForFree: true,
        datePublished: post.publishedAt ?? undefined,
        dateModified: post.updatedAt ?? post.publishedAt ?? undefined,
        author: post.authorName
          ? {
              '@type': 'Person' as const,
              name: post.authorName,
            }
          : { '@id': 'https://www.emovia.com/#organization' },
        publisher: { '@id': 'https://www.emovia.com/#organization' },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': postUrl,
        },
        ...(post.ogImage?.url ? { image: post.ogImage.url } : {}),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.emovia.com/' },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.emovia.com/blog' },
          { '@type': 'ListItem', position: 3, name: post.title, item: postUrl },
        ],
      },
    ];
  }, [post, seoDescription]);

  if (error === 'not_found' || !post) {
    return (
      <PublicLayout forceFullReloadLinks>
        <Head title="Articolo non trovato">
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <section className="py-20">
          <div className="container max-w-3xl text-center space-y-6">
            <Heading level={1}>Articolo non trovato.</Heading>
            <CardDescription>
              L’articolo richiesto non è disponibile oppure è stato rimosso.
            </CardDescription>
            <Button asChild>
              <a href="/blog">Tutti gli articoli</a>
            </Button>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout forceFullReloadLinks>
      <PublicSeo
        title={`${post.seoTitle ?? post.title} | Emovia Blog`}
        description={seoDescription}
        path={`/blog/${post.slug}`}
        imageUrl={post.ogImage?.url}
        type="article"
        jsonLd={jsonLd ?? undefined}
      >
        {post.publishedAt && (
          <meta property="article:published_time" content={post.publishedAt} />
        )}
        {post.updatedAt && <meta property="article:modified_time" content={post.updatedAt} />}
        {post.authorName && <meta property="article:author" content={post.authorName} />}
      </PublicSeo>
      <article className="bg-linear-to-b from-background to-primary/5 py-16">
        <div className="container">
          <div className="mx-auto max-w-5xl space-y-10">
          <header className="space-y-4 text-center">
            <div className="flex items-center justify-center gap-3">
              {post.newBadge && <Badge variant="secondary">New</Badge>}
              {post.publishedAt && (
                <p className="text-muted-foreground">{formatFullBlogDate(post.publishedAt)}</p>
              )}
            </div>
            <Heading level={1}>{post.title}</Heading>
            {post.caption && <CardDescription>{post.caption}</CardDescription>}
            {post.authorName && (
              <div className="flex items-center justify-center gap-3">
                {post.authorAvatarUrl && (
                  <img
                    src={post.authorAvatarUrl}
                    alt={post.authorName}
                    className="h-10 w-10 rounded-full object-cover border border-border"
                    width={40}
                    height={40}
                  />
                )}
                <div className="text-left">
                  <p className="text-sm font-medium text-foreground">{post.authorName}</p>
                  {post.authorRole && (
                    <p className="text-xs text-muted-foreground">{post.authorRole}</p>
                  )}
                </div>
              </div>
            )}
          </header>

          {post.ogImage?.url && (
            <Card variant="outlined" className="overflow-hidden">
              <img
                src={post.ogImage.url}
                alt={post.title}
                className="w-full object-cover aspect-video"
                loading="lazy"
              />
            </Card>
          )}

          <div className="space-y-10">
            {(post.contentBlocks ?? []).map((block, index) => {
              switch (block.type) {
                case 'hero':
                  return (
                    <Card key={index} semantic="primary">
                      <CardHeader className="space-y-3">
                        <CardTitle headingLevel="h2">{block.title}</CardTitle>
                        {block.subtitle && <CardDescription>{block.subtitle}</CardDescription>}
                      </CardHeader>
                      {block.cta_label && block.cta_href && (
                        <CardFooter className="border-t border-border">
                          <Button asChild>
                            <a href={block.cta_href}>{block.cta_label}</a>
                          </Button>
                        </CardFooter>
                      )}
                    </Card>
                  );
                case 'rich_text':
                  return (
                    <div key={index} className="space-y-4">
                      {toArray(block.doc?.content).map(renderNode)}
                    </div>
                  );
                case 'image': {
                  const image = contentImageMap.get(block.attachment_id);
                  if (!image) return null;
                  return (
                    <figure key={index} className="space-y-3">
                      <Card variant="outlined" className="overflow-hidden">
                        <img
                          src={image.url}
                          alt={block.alt}
                          className="w-full object-cover"
                          loading="lazy"
                        />
                      </Card>
                      {block.caption && <CardDescription>{block.caption}</CardDescription>}
                    </figure>
                  );
                }
                case 'quote':
                  return (
                    <Card key={index} semantic="primary">
                      <CardContent>
                        <div className="space-y-2">
                          <p className="text-foreground">&ldquo;{block.text}&rdquo;</p>
                          {(block.author || block.role || block.source) && (
                            <CardDescription>
                              {block.author}
                              {block.role ? `, ${block.role}` : ''}
                              {block.source && (
                                <>
                                  {block.author || block.role ? ' — ' : ''}
                                  {block.source_url ? (
                                    <InlineLink href={block.source_url} target="_blank" rel="noreferrer">
                                      {block.source}
                                    </InlineLink>
                                  ) : (
                                    <span className="italic">{block.source}</span>
                                  )}
                                </>
                              )}
                            </CardDescription>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                case 'cta':
                  return (
                    <Card key={index}>
                      <CardHeader>
                        <CardTitle headingLevel="h3">{block.title}</CardTitle>
                      </CardHeader>
                      {block.body && (
                        <CardContent>
                          <CardDescription>{block.body}</CardDescription>
                        </CardContent>
                      )}
                      <CardFooter>
                        <Button asChild>
                          <a href={block.cta_href}>{block.cta_label}</a>
                        </Button>
                      </CardFooter>
                    </Card>
                  );
                default:
                  return null;
              }
            })}
          </div>

          <footer className="space-y-6">
            <Button asChild variant="outline">
              <a href="/blog">Tutti gli articoli</a>
            </Button>

            {related.length > 0 && (
              <section className="space-y-4">
                <Heading level={2}>Altri articoli</Heading>
                <div className="grid gap-4 md:grid-cols-3">
                  {related.map((entry) => (
                    <Card key={entry.id} variant="interactive">
                      <CardHeader>
                        <CardTitle headingLevel="h3">{entry.title}</CardTitle>
                        {entry.publishedAt && (
                          <CardDescription>{formatShortBlogDate(entry.publishedAt)}</CardDescription>
                        )}
                      </CardHeader>
                      <CardFooter>
                        <Button asChild variant="outline" size="sm">
                          <a href={`/blog/${entry.slug}`}>Leggi</a>
                        </Button>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              </section>
            )}
          </footer>
          </div>
        </div>
      </article>
    </PublicLayout>
  );
}
